import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Buckets } from 'ontraccr-common';

import { getSortedCustomers, getFormattedAndSortedProjects, getProjectIdMap } from '../state/forms.selectors';

export default function useFormDropdownOptions({
  divisions,
  projectId,
  selectedBucket,
  selectedBuckets,
  customerId,
  shouldComputeCustomers = true,
  shouldComputeProjects = true,
}) {
  // Get pre-sorted data from Redux (only sorts/maps when data changes!)
  const sortedCustomers = useSelector(getSortedCustomers);
  const sortedProjects = useSelector(getFormattedAndSortedProjects);
  const projectIdMap = useSelector(getProjectIdMap);

  const divSet = useMemo(() => new Set(divisions), [divisions]);

  // Prefer all selected buckets (UNION/OR). Fall back to singular selectedBucket.
  // Sorted key keeps customer/project memos stable across array identity/order churn.
  const selectedBucketIdsKey = useMemo(() => {
    let ids = [];
    if (selectedBuckets?.length) {
      ids = selectedBuckets.map((bucket) => bucket.id);
    } else if (selectedBucket?.id) {
      ids = [selectedBucket.id];
    }
    return Array.from(new Set(ids)).sort().join(',');
  }, [selectedBuckets, selectedBucket]);

  const {
    customerOptions,
    smartFilteredCustomers,
  } = useMemo(() => {
    // Skip expensive filtering if customers not used in form
    if (!shouldComputeCustomers || !sortedCustomers) {
      return { customerOptions: [], smartFilteredCustomers: [] };
    }

    let bucketsForFilter = [];
    if (selectedBuckets?.length) {
      bucketsForFilter = selectedBuckets;
    } else if (selectedBucket) {
      bucketsForFilter = [selectedBucket];
    }

    const {
      [projectId]: { customerId: projectCustomerId } = {},
    } = projectIdMap ?? {};
    const baseCustomers = [];
    const sfCustomers = [];

    // No sorting needed - already sorted! Just filter (O(n) - fast!)
    sortedCustomers.forEach((customer) => {
      if (
        !customer.active
        || !customer?.divisionIds?.some((divisionId) => divSet.has(divisionId))
      ) return;
      const hasProjectMatch = !projectId || customer.id === projectCustomerId;
      // UNION: customer matches if linked to any selected bucket
      const hasBucketMatch = !bucketsForFilter.length
        || Buckets.getLinkedBuckets(bucketsForFilter, [customer.id], ['customers']).allBuckets.length > 0;
      const shouldAddToSmartFilterList = hasProjectMatch && hasBucketMatch;
      baseCustomers.push(customer);
      if (shouldAddToSmartFilterList) sfCustomers.push(customer);
    });
    return { customerOptions: baseCustomers, smartFilteredCustomers: sfCustomers };
  }, [
    shouldComputeCustomers,
    sortedCustomers,
    projectIdMap,
    projectId,
    selectedBucketIdsKey,
    divSet,
  ]);

  const {
    projectOptions,
    smartFilteredProjects,
  } = useMemo(() => {
    // Skip expensive filtering if projects not used in form
    if (!shouldComputeProjects || !sortedProjects) {
      return { projectOptions: [], smartFilteredProjects: [] };
    }

    let bucketsForFilter = [];
    if (selectedBuckets?.length) {
      bucketsForFilter = selectedBuckets;
    } else if (selectedBucket) {
      bucketsForFilter = [selectedBucket];
    }

    const baseProjects = [];
    const sfProjects = [];

    // No sorting needed - already sorted! Just filter (O(n) - fast!)
    sortedProjects.forEach((project) => {
      if (!project.active || !divSet.has(project.divisionId)) return;
      const hasCustomerMatch = !customerId || project.customerId === customerId;
      // UNION: project matches if linked to any selected bucket
      const hasBucketMatch = !bucketsForFilter.length
        || Buckets.getLinkedBuckets(bucketsForFilter, [project.id], ['projects']).allBuckets.length > 0;
      const shouldAddToSmartFilterList = hasCustomerMatch && hasBucketMatch;
      baseProjects.push(project);
      if (shouldAddToSmartFilterList) sfProjects.push(project);
    });

    return { projectOptions: baseProjects, smartFilteredProjects: sfProjects };
  }, [
    shouldComputeProjects,
    sortedProjects,
    selectedBucketIdsKey,
    customerId,
    divSet,
  ]);

  return {
    customerOptions,
    smartFilteredCustomers,
    projectOptions,
    smartFilteredProjects,
  };
}
